import styled from '@emotion/styled';
import { useRef, useEffect } from 'react';

import { ToastManager } from '../../../ToastManager';
import { IconButton } from '../../buttons/IconButton';
import refresh from '../images/button/refresh.svg';
import default_camera_image from '../images/camera/default_camera_image.png';

import { useEventCallback } from '../../../hooks/useEventCallback';
import { useI18n } from '../../../hooks/useI18nFormatters';
import { useImage } from '../../../store/images/useImages';

import { su } from '../../../theme/functions/su.ts';
import { theme } from '../../../theme/theme';

import { getSnapshotImageUrl, type SnapshotImageData } from './getSnapshotImageUrl';

interface FetchImageOptions {
  bustCache?: boolean;
}

export interface SnapshotViewProps {
  source?: {
    image?: SnapshotImageData;
    imageId?: string | null;
  };
  baseUrl?: string;
}

export function SnapshotView(props: SnapshotViewProps) {
  const imageRef = useRef<HTMLImageElement>(null);
  const requestIdRef = useRef(0);
  const { i18n } = useI18n();

  const { image } = useImage({ imageId: props.source?.imageId ?? undefined });

  const fetchImage = useEventCallback(async (options: FetchImageOptions = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const imageUrl = getSnapshotImageUrl({
      baseUrl: props.baseUrl,
      image: image ?? props.source?.image,
      bustCache: options.bustCache === true,
    });

    if (imageUrl == null) {
      return;
    }

    try {
      const response = await fetch(imageUrl);

      if (requestId !== requestIdRef.current || imageRef.current == null) {
        return;
      }

      if (response.ok) {
        imageRef.current.src = imageUrl;
        return;
      }

      const errorData = await response.json();

      if (requestId !== requestIdRef.current || imageRef.current == null) {
        return;
      }

      imageRef.current.src = default_camera_image;
      ToastManager.handleError(errorData);
    } catch (err) {
      if (requestId !== requestIdRef.current || imageRef.current == null) {
        return;
      }

      imageRef.current.src = default_camera_image;
      console.error('SnapshotView: Failed to load the image', err);
      ToastManager.handleError({ message: 'An error occurred while loading the image.' });
    }
  });

  const handleRefreshPress = useEventCallback(() => {
    fetchImage({ bustCache: true });
  });

  useEffect(() => {
    fetchImage();

    return () => {
      requestIdRef.current += 1;
    };
  }, [image, props.source?.image, props.baseUrl, fetchImage]);

  return (
    <S.Container>
      <S.Image
        ref={imageRef}
        src={default_camera_image}
        onError={() => {
          if (imageRef.current != null) {
            imageRef.current.src = default_camera_image;
          }
          ToastManager.handleError({ message: 'An error occurred while loading the image.' });
        }}
      />

      <S.Controls>
        <IconButton
          aria-label={i18n.messageFormatter('device.camera.refreshSnapshot')}
          url={refresh}
          color={theme.color.white}
          onPress={handleRefreshPress}
        />
      </S.Controls>
    </S.Container>
  );
}

function S() {}
SnapshotView.S = S;

S.Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

S.Image = styled.img`
  width: 320px;
  aspect-ratio: 1 / 1;
  border-radius: ${theme.borderRadius.default};
  margin-bottom: ${su(2)};
  object-fit: contain;
  background-color: ${theme.color.black};
`;

S.Controls = styled.div`
  margin-top: 28px;
  margin-bottom: ${su(2)};
`;
